import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { crmDealsApi, crmPipelineApi, crmContactsApi, crmOrgsApi } from '../../api/crm';
import { productLinesApi } from '../../api/productLines';
import { companiesApi } from '../../api/companies';
import { useClassifications } from '../../hooks/useClassifications';
import Field from './Field';
import { aiCrmApi } from '../../api/aiCrm';
import { Sparkles } from 'lucide-react';
import { useCompany } from '../../contexts/CompanyContext';
import Modal from '../Modal';
import { invalidateCrm } from '../../lib/queryCache';
import type { CrmDeal } from '../../types';

interface Props {
  deal?: CrmDeal;
  defaultStageId?: string;
  onClose: () => void;
  onSaved: (id: string) => void;
}

export default function DealForm({ deal, defaultStageId, onClose, onSaved }: Props) {
  const { currentCompany } = useCompany();
  const cid = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;

  const [form, setForm] = useState({
    title: deal?.title ?? '',
    value: deal?.value?.toString() ?? '',
    currency: deal?.currency ?? 'USD',
    stageId: deal?.stageId ?? defaultStageId ?? '',
    contactId: deal?.contactId ?? '',
    organizationId: deal?.organizationId ?? '',
    expectedCloseDate: deal?.expectedCloseDate ? deal.expectedCloseDate.slice(0, 10) : '',
    description: deal?.description ?? '',
    probability: deal?.probability?.toString() ?? '',
    tags: deal?.tags?.join(', ') ?? '',
    source: deal?.source ?? '',
    productLineId: deal?.productLineId ?? '',
    nextAction: deal?.nextAction ?? '',
    nextActionDate: deal?.nextActionDate ? deal.nextActionDate.slice(0, 10) : '',
    nextActionOwnerId: deal?.nextActionOwnerId ?? '',
  });
  const [error, setError] = useState('');

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const { data: stages } = useQuery({ queryKey: ['crm-pipeline', cid], queryFn: () => crmPipelineApi.list(), enabled: !!cid });
  const { data: contacts } = useQuery({ queryKey: ['crm-contacts-pick', cid], queryFn: () => crmContactsApi.list({ limit: 200 }), enabled: !!cid });
  const { data: orgs } = useQuery({ queryKey: ['crm-orgs-pick', cid], queryFn: () => crmOrgsApi.list({ limit: 200 }), enabled: !!cid });
  // Product lines already exist for tickets; a deal just points at one. Nothing
  // here assumes what the company sells.
  const { data: productLines } = useQuery({ queryKey: ['product-lines', cid], queryFn: () => productLinesApi.list(), enabled: !!cid });
  const { data: members } = useQuery({ queryKey: ['company-members', cid], queryFn: () => companiesApi.getMembers(cid), enabled: !!cid });
  const staff = ((members as any[]) || []).filter(m => ['owner', 'admin', 'technician'].includes(m.role) && m.isActive !== false);
  const { optionsFor } = useClassifications();

  // Suggesting a next step is the one place AI touches a form. It fills the
  // fields and stops: the user edits and saves, so nothing is written that
  // somebody did not read first. Hidden entirely when no provider is set up.
  const { data: aiAvailable } = useQuery({
    queryKey: ['ai-crm-status', cid],
    queryFn: () => aiCrmApi.status(),
    enabled: !!cid && !!deal,
    staleTime: 5 * 60 * 1000,
  });
  const [aiWhy, setAiWhy] = useState('');
  const suggest = useMutation({
    mutationFn: () => aiCrmApi.nextAction(deal!.id),
    onSuccess: (r) => {
      if (!r.suggestion) { setAiWhy(r.error ?? 'No suggestion — there is not enough history yet.'); return; }
      setForm(f => ({
        ...f,
        nextAction: r.suggestion!.action,
        nextActionDate: r.suggestion!.dueDate ?? f.nextActionDate,
      }));
      setAiWhy(r.suggestion.why);
    },
    onError: () => setAiWhy('The AI provider did not answer.'),
  });

  const queryClient = useQueryClient();

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: any = {
        title: form.title,
        stageId: form.stageId,
        value: form.value ? parseFloat(form.value) : undefined,
        currency: form.currency,
        contactId: form.contactId || undefined,
        organizationId: form.organizationId || undefined,
        expectedCloseDate: form.expectedCloseDate || undefined,
        description: form.description || undefined,
        probability: form.probability ? parseFloat(form.probability) : undefined,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        source: form.source || null,
        productLineId: form.productLineId || null,
        nextAction: form.nextAction || null,
        nextActionDate: form.nextActionDate || null,
        nextActionOwnerId: form.nextActionOwnerId || null,
      };
      return deal ? crmDealsApi.update(deal.id, payload) : crmDealsApi.create(payload as any);
    },
    onSuccess: (saved) => { invalidateCrm(queryClient); onSaved(saved.id); },
    onError: (e: any) => setError(e.response?.data?.message ?? 'Failed to save'),
  });

  const footer = (
    <>
      <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !form.title || !form.stageId}>
        {saveMutation.isPending ? 'Saving…' : deal ? 'Save Changes' : 'Create Deal'}
      </button>
    </>
  );

  return (
    <Modal open onClose={onClose} title={deal ? 'Edit Deal' : 'New Deal'} width={560} busy={saveMutation.isPending} footer={footer}>
      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
        {error && <div className="text-[13px] text-err bg-primary-muted rounded-lg p-3">{error}</div>}

        <div>
          <label className="label">Deal Title *</label>
          <input className="input w-full" value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Enterprise Plan — Acme Corp" />
        </div>

        <div>
          <label className="label">Pipeline Stage *</label>
          <select className="select w-full" value={form.stageId} onChange={e => set('stageId', e.target.value)}>
            <option value="">— select stage —</option>
            {stages?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label">Value</label>
            <input className="input w-full" type="number" min="0" value={form.value} onChange={e => set('value', e.target.value)} placeholder="0" />
          </div>
          <div>
            <label className="label">Currency</label>
            <select className="select w-full" value={form.currency} onChange={e => set('currency', e.target.value)}>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="CAD">CAD</option>
              <option value="AUD">AUD</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label">Contact</label>
            <select className="select w-full" value={form.contactId} onChange={e => set('contactId', e.target.value)}>
              <option value="">— none —</option>
              {contacts?.contacts.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Organization</label>
            <select className="select w-full" value={form.organizationId} onChange={e => set('organizationId', e.target.value)}>
              <option value="">— none —</option>
              {orgs?.organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label">Expected Close Date</label>
            <input className="input w-full" type="date" value={form.expectedCloseDate} onChange={e => set('expectedCloseDate', e.target.value)} />
          </div>
          <div>
            <label className="label">Win Probability (%)</label>
            <input className="input w-full" type="number" min="0" max="100" value={form.probability} onChange={e => set('probability', e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Source">
            <select className="select w-full" value={form.source} onChange={e => set('source', e.target.value)}>
              <option value="">— not recorded —</option>
              {optionsFor('lead_source', deal?.source).map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Product / service">
            <select className="select w-full" value={form.productLineId} onChange={e => set('productLineId', e.target.value)}>
              <option value="">— none —</option>
              {(productLines ?? []).filter((p: any) => p.isActive !== false).map((p: any) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </Field>
        </div>

        {/* What happens next — the one thing an open deal should always answer. */}
        <div className="rounded-lg border border-line p-3 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted">What happens next</p>
            {aiAvailable && deal && (
              <button
                type="button"
                className="btn btn-secondary text-xs inline-flex items-center gap-1"
                onClick={() => suggest.mutate()}
                disabled={suggest.isPending}
              >
                <Sparkles className="w-3 h-3" /> {suggest.isPending ? 'Thinking…' : 'Suggest'}
              </button>
            )}
          </div>
          {aiWhy && <p className="text-[11px] text-muted">{aiWhy} — edit it before saving if it is off.</p>}
          <Field label="Next action" hint="One line. It shows on the board, on the account, and in My work.">
            <input className="input w-full" value={form.nextAction} onChange={e => set('nextAction', e.target.value)} placeholder="e.g. Send the quote, Confirm the call time" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Next action date">
              <input className="input w-full" type="date" value={form.nextActionDate} onChange={e => set('nextActionDate', e.target.value)} />
            </Field>
            <Field label="Next action owner">
              <select className="select w-full" value={form.nextActionOwnerId} onChange={e => set('nextActionOwnerId', e.target.value)}>
                <option value="">— whoever owns the deal —</option>
                {staff.map((m: any) => {
                  const uid = m.user?.id ?? m.userId;
                  return <option key={uid} value={uid}>{m.user?.name || m.user?.email}</option>;
                })}
              </select>
            </Field>
          </div>
        </div>

        <div>
          <label className="label">Tags <span className="text-muted font-normal">(comma-separated)</span></label>
          <input className="input w-full" value={form.tags} onChange={e => set('tags', e.target.value)} />
        </div>

        <div>
          <label className="label">Description</label>
          <textarea className="input w-full resize-y h-20" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Additional context…" />
        </div>
      </div>
    </Modal>
  );
}
